import React, { useState, useEffect, useRef } from "react"; // Add useRef to imports
import { Link, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  getFirestore,
  getDocs,
  doc,
  query,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useAuth } from "../context/AuthContext";

import SelectMenu from "../components/crm/SelectMenu";
export default function PhoneVerification() {
  const navigate = useNavigate();
  const functions = getFunctions();
  const db = getFirestore();
  const { globalUser } = useAuth();

  // State
  const [country, setCountry] = useState("IN");
  const [nationalNumber, setNationalNumber] = useState("");
  const [phoneNumberValue, setPhoneNumberValue] = useState("");
  const [verifiedE164Number, setVerifiedE164Number] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoadingSend, setIsLoadingSend] = useState(false);
  const [isLoadingVerify, setIsLoadingVerify] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Ref to prevent double initialization
  const hasInitialized = useRef(false);

  // Firebase Function Callers
  const sendOtpFunction = httpsCallable(functions, "sendOtp");
  const verifyOtpFunction = httpsCallable(functions, "verifyOtp");

  // Same duplicate check as Flutter isMobileNumberRegistered —
  // queries both lawyers + client collections, excludes current user's own doc
  const isMobileNumberRegistered = async (nationalNum) => {
    const currentUid = globalUser?.uid;
    const lawyerSnap = await getDocs(query(collection(db, "lawyers"), where("mobileNumber", "==", nationalNum)));
    for (const d of lawyerSnap.docs) {
      if (d.id !== currentUid) return true;
    }
    const clientSnap = await getDocs(query(collection(db, "client"), where("mobileNumber", "==", nationalNum)));
    for (const d of clientSnap.docs) {
      if (d.id !== currentUid) return true;
    }
    return false;
  };

  // Handle sending OTP
  const handleSendOtp = async (targetNumber = null) => {
    setError(null);
    setSuccessMessage(null);

    if (!globalUser) {
      setError("User not logged in. Please log in again.");
      setIsInitializing(false);
      return;
    }

    const targetNumberE164 = targetNumber || phoneNumberValue;

    if (!targetNumberE164 || !isValidPhoneNumber(targetNumberE164)) {
      setError("Please enter a valid phone number including country code.");
      setIsInitializing(false);
      return;
    }

    setIsLoadingSend(true);

    try {
      // Extract national number for duplicate check (same as Flutter)
      let nationalNum = nationalNumber;
      if (!nationalNum && targetNumberE164) {
        try {
          const parsed = parsePhoneNumber(targetNumberE164);
          nationalNum = parsed?.nationalNumber || "";
        } catch {}
      }

      // Duplicate check before sending OTP
      if (nationalNum) {
        const alreadyRegistered = await isMobileNumberRegistered(nationalNum);
        if (alreadyRegistered) {
          setError("This phone number is already registered with another account.");
          setIsLoadingSend(false);
          setIsInitializing(false);
          return;
        }
      }

      const response = await sendOtpFunction({ phoneNumber: targetNumberE164 });
      console.log("Send OTP response:", response);
      setSuccessMessage("OTP sent successfully!");
      setVerifiedE164Number(targetNumberE164);
      setOtp("");
      setIsOtpSent(true);
    } catch (err) {
      console.error("Error sending OTP:", err);
      setError(err.message || "Failed to send OTP. Please try again.");
      setIsOtpSent(false);
      setVerifiedE164Number("");
    } finally {
      setIsLoadingSend(false);
      setIsInitializing(false);
    }
  };

  // Update phoneNumberValue based on country and nationalNumber
  useEffect(() => {
    if (country && nationalNumber) {
      const callingCode = getCountryCallingCode(country);
      const fullNumber = `+${callingCode}${nationalNumber}`;
      if (isValidPhoneNumber(fullNumber)) {
        setPhoneNumberValue(fullNumber);
      } else {
        setPhoneNumberValue("");
      }
    } else {
      setPhoneNumberValue("");
    }
  }, [country, nationalNumber]);

  // Check localStorage and send OTP on component mount
  useEffect(() => {
    if (hasInitialized.current) return; // Skip if already initialized
    hasInitialized.current = true; // Mark as initialized

    const checkStoredNumberAndSendOTP = async () => {
      const storedNumber = localStorage.getItem("mobileWithCountryCode");
      console.log("Stored number from local storage:", storedNumber);

      if (storedNumber && isValidPhoneNumber(storedNumber)) {
        try {
          const parsed = parsePhoneNumber(storedNumber);
          if (parsed) {
            setCountry(parsed.country);
            setNationalNumber(parsed.nationalNumber);
            setPhoneNumberValue(storedNumber);
            await handleSendOtp(storedNumber);
          } else {
            console.error("Failed to parse stored phone number:", storedNumber);
            setError("Invalid stored phone number format.");
            setIsInitializing(false);
          }
        } catch (err) {
          console.error("Error processing stored phone number:", err);
          setError("Error retrieving stored phone number.");
          setIsInitializing(false);
        }
      } else {
        setIsInitializing(false);
      }
    };

    checkStoredNumberAndSendOTP();
  }, []);

  // Reset state on user change
  useEffect(() => {
    setCountry("IN");
    setNationalNumber("");
    setPhoneNumberValue("");
    setVerifiedE164Number("");
    setOtp("");
    setIsOtpSent(false);
    setError(null);
    setSuccessMessage(null);
    setIsLoadingSend(false);
    setIsLoadingVerify(false);
  }, [globalUser]);

  // Event Handlers
  const handleResendOtp = async () => {
    if (!verifiedE164Number) {
      setError("Cannot resend OTP without a verified number.");
      return;
    }
    setIsLoadingSend(true);
    setError(null);
    setSuccessMessage(null);
    console.log(`Requesting OTP Resend for: ${verifiedE164Number}`);

    try {
      const response = await sendOtpFunction({
        phoneNumber: verifiedE164Number,
      });
      console.log("Resend OTP response:", response);
      console.log(`OTP Resend request successful for: ${verifiedE164Number}`);
      setSuccessMessage("New OTP sent successfully!");
      setOtp("");
    } catch (err) {
      console.error("Error resending OTP:", err);
      setError(err.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsLoadingSend(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  const handleVerifyOtp = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!otp || otp.length !== 6) {
      setError("Please enter the 6-digit OTP.");
      return;
    }
    if (!verifiedE164Number) {
      setError("Mobile number is missing. Cannot verify.");
      return;
    }
    if (!globalUser) {
      setError("User not logged in. Please log in again.");
      return;
    }

    setIsLoadingVerify(true);
    console.log(`Verifying OTP ${otp} for: ${verifiedE164Number}`);

    try {
      const result = await verifyOtpFunction({
        phoneNumber: verifiedE164Number,
        otp: otp,
      });
      console.log("Verification result:", result);

      if (result.data.success) {
        console.log("OTP Verification successful.");
        setSuccessMessage("Verification successful! Updating profile...");

        try {
          const userType = localStorage.getItem("userType");
          if (!userType || (userType !== "lawyers" && userType !== "client")) {
            throw new Error("User type missing, cannot update profile.");
          }

          const parsedNumber = parsePhoneNumber(verifiedE164Number);
          if (!parsedNumber) {
            throw new Error("Failed to parse verified phone number.");
          }
          const countryCode = `+${parsedNumber.countryCallingCode}`;
          const nationalNum = parsedNumber.nationalNumber;

          const userDocRef = doc(db, userType, globalUser.uid);

          console.log(
            `Updating Firestore document: ${userType}/${globalUser.uid} after verification.`
          );
          console.log(
            `With: countryCode=${countryCode}, mobileNumber=${nationalNum}, mobileWithCountryCode=${verifiedE164Number}, mobileVerified=true`
          );

          await updateDoc(userDocRef, {
            countryCode: countryCode,
            mobileNumber: nationalNum,
            mobileWithCountryCode: verifiedE164Number,
            mobileVerifiedAt: serverTimestamp(),
          });

          console.log(
            "Firestore profile updated successfully after verification."
          );
          setSuccessMessage("Verification successful! Profile updated.");
          navigate("/portal/Dashboard");
        } catch (updateError) {
          console.error("Error updating Firestore profile:", updateError);
          setError("Verification successful, but failed to update profile.");
          navigate("/portal/Dashboard");
        }
      } else {
        console.log("OTP Verification failed:", result.data.message);
        setError(
          result.data.message || "Verification failed. Please check the code."
        );
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setError(err.message || "Verification failed. Please try again.");
    } finally {
      setIsLoadingVerify(false);
    }
  };

  // Render Logic
  const renderLoadingState = () => (
    <div className="flex flex-col gap-5 max-w-xs w-full items-center">
      <div className="w-16 h-16 border-4 border-t-4 border-[#C99F4A] border-t-transparent rounded-full animate-spin"></div>
      <p className="text-center text-gray-300 mt-1 text-sm px-4">
        Sending verification code to your registered mobile number...
      </p>
    </div>
  );

  const renderMobileInput = () => (
    <div className="flex flex-col gap-5 max-w-xs w-full items-center">
      <p className="text-center text-gray-300 mt-1 text-sm px-4">
        Enter your mobile number with country code to receive a verification
        code.
      </p>
      <div className="flex items-center p-1 bg-white text-black rounded-lg justify-between gap-1">
        <div className="flex items-center p-1 text-black rounded-lg border-2">
          <SelectMenu
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-24 pl-2 focus:outline-none"
            disabled={isLoadingSend}
          >
            {getCountries().map((countryCode) => (
              <option key={countryCode} value={countryCode}>
                {countryCode} (+{getCountryCallingCode(countryCode)})
              </option>
            ))}
          </SelectMenu>
        </div>
        <div className="flex items-center p-1 text-black rounded-lg border-2">
          <input
            type="tel"
            value={nationalNumber}
            onChange={(e) => setNationalNumber(e.target.value)}
            placeholder="Phone Number"
            className="w-full pl-2 focus:outline-none"
            disabled={isLoadingSend}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={() => handleSendOtp()}
        className={`w-full bg-green-600 p-2.5 rounded-lg text-white font-semibold hover:bg-green-700 text-lg mt-2 ${
          isLoadingSend || !phoneNumberValue
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
        disabled={isLoadingSend || !phoneNumberValue}
      >
        {isLoadingSend ? "Sending OTP..." : "Send OTP"}
      </button>
    </div>
  );

  const renderOtpInput = () => (
    <div className="flex flex-col gap-5 max-w-xs w-full">
      <p className="text-center text-gray-300 mt-1 text-sm">
        Enter the 6-digit OTP sent to{" "}
        <span className="font-semibold text-white">{verifiedE164Number}</span>
      </p>
      <input
        type="text"
        placeholder="6 digit code"
        value={otp}
        onChange={handleOtpChange}
        maxLength="6"
        className="w-full p-3 bg-white text-black rounded-lg focus:outline-none text-center tracking-[0.3em] placeholder-gray-500"
        disabled={isLoadingVerify || isLoadingSend}
      />
      <button
        type="button"
        onClick={handleVerifyOtp}
        className={`w-full bg-blue-600 p-2.5 rounded-lg text-white font-semibold hover:bg-blue-700 text-lg ${
          isLoadingVerify ? "opacity-50 cursor-not-allowed" : ""
        }`}
        disabled={isLoadingVerify || isLoadingSend}
      >
        {isLoadingVerify ? "Verifying..." : "Verify OTP"}
      </button>
      <p className="text-center text-white text-sm mt-3">
        Didn't get code?{" "}
        <button
          onClick={handleResendOtp}
          disabled={isLoadingVerify || isLoadingSend}
          className={`text-[#C99F4A] hover:underline focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
            isLoadingSend ? "animate-pulse" : ""
          }`}
        >
          {isLoadingSend ? "Sending..." : "Resend OTP"}
        </button>
      </p>
    </div>
  );

  // Determine what to render based on states
  const renderContent = () => {
    if (isInitializing) {
      return renderLoadingState();
    } else if (isOtpSent) {
      return renderOtpInput();
    } else {
      return renderMobileInput();
    }
  };

  return (
    <div className="min-h-screen overflow-hidden py-10 sm:py-0 flex flex-col bg-[#060223] items-center justify-center relative">
      <img
        src="../ellipse-top.png"
        className="absolute rotate-27 right-[-25vw] top-[-20vh] sm:right-[-150px] sm:top-[-200px] z-0"
        alt=""
      />
      <img
        src="../ellipse-bottom.png"
        className="absolute sm:bottom-[-300px] bottom-[-20vh] z-0"
        alt=""
      />
      <img
        src="../JURIDENT.png"
        className="absolute opacity-0 sm:opacity-[1] left-5 top-5 z-0"
        alt=""
      />
      <img
        src="../Jurident-Logo-bottom.png"
        className="absolute opacity-0 sm:opacity-[1] right-5 bottom-5 z-0"
        alt=""
      />

      {/* Content Box */}
      <div className="flex flex-col gap-3 items-center p-5 z-10 rounded-lg w-full max-w-md bg-[#060223]/70 backdrop-blur-sm">
        <img
          src="../EmailVerification.png"
          alt="Verification Icon"
          className="w-[80px] sm:w-[100px]"
        />
        <h2 className="text-3xl sm:text-5xl font-semibold text-[#C99F4A] text-center mb-4">
          Mobile Verification
        </h2>

        {error && !isInitializing && (
          <p className="text-red-500 text-center mt-1 mb-3 text-sm font-medium bg-red-100/20 px-3 py-1 rounded">
            {error}
          </p>
        )}
        {successMessage && !error && !isInitializing && (
          <p className="text-green-500 text-center mt-1 mb-3 text-sm font-medium bg-green-100/20 px-3 py-1 rounded">
            {successMessage}
          </p>
        )}

        {renderContent()}

        {!isOtpSent && !isInitializing && (
          <Link
            to={localStorage.getItem("userType") === "client" ? "../ClientSignIn" : "../LawyerSignIn"}
            className="text-gray-400 hover:text-white mt-5 text-sm z-10"
          >
            Back to Login
          </Link>
        )}
      </div>
    </div>
  );
}
